/**
 * battery.js — one definition of what a battery reading MEANS.
 *
 * The 60 / 25 thresholds were duplicated verbatim in Dashboard.jsx and
 * MyRobots.jsx, each with its own `battColor`. Two copies of a rule that
 * decides when a robot reads as critical is one copy too many, so both now
 * import from here.
 *
 * `battLevel` is always a PERCENTAGE (0-100) by the time it reaches these
 * helpers. That matters because the two socket events feeding it disagree:
 * BATTERY_STATUS_UPDATE sends `data.batteryLevel` already as a percentage,
 * while SENSOR_STATUS_UPDATE sends `battery` as a 0-1 fraction which its
 * handler multiplies by 100 before storing. Normalising at the source keeps
 * that conversion out of every render site.
 */
import { tokens } from "../theme";

/** Level at or above which the battery reads as healthy. */
const GOOD_MIN = 60;
/** Level at or above which the battery reads as low rather than critical. */
const LOW_MIN = 25;

/**
 * The battery's condition as a stable key — "good" | "low" | "critical".
 *
 * Returns null for a missing reading so callers can render their own "—"
 * placeholder rather than being handed a status that claims to know something.
 */
export const battStatusKey = (level) => {
  if (typeof level !== "number" || Number.isNaN(level)) return null;
  if (level > GOOD_MIN) return "good";
  if (level >= LOW_MIN) return "low";
  return "critical";
};

/** Human label for a status key. */
const STATUS_LABELS = { good: "Good", low: "Low", critical: "Critical" };

/** The battery's condition as a display label, or null when unknown. */
export const battStatusLabel = (level) => STATUS_LABELS[battStatusKey(level)] ?? null;

/**
 * Bar/readout colour for a level. Same three bands as the status above — they
 * are the same judgement, so they cannot be allowed to drift apart.
 */
export const battColor = (level) => {
  const key = battStatusKey(level);
  if (key === "good") return tokens.success;
  if (key === "low") return tokens.warning;
  return tokens.danger;
};

/**
 * A full sentence for assistive tech and the native tooltip: "Battery 47.5%,
 * Low, charging". Colour alone carries the status visually, which a
 * colour-blind user cannot read — this is what makes it available to everyone.
 */
export const battAriaLabel = (level, charging = false) => {
  if (typeof level !== "number" || Number.isNaN(level)) return "Battery level unknown";
  const parts = [`Battery ${level.toFixed(1)}%`];
  const label = battStatusLabel(level);
  if (label) parts.push(label);
  if (charging) parts.push("charging");
  return parts.join(", ");
};

/**
 * Is this a state worth calling out in text? "Good" is the default condition
 * and printing it on every healthy robot is noise, so only the states that
 * want attention get a visible label.
 */
export const battNeedsAttention = (level) => {
  const key = battStatusKey(level);
  return key === "low" || key === "critical";
};
